import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

export default function ScreenerResults({ refreshKey }) {
  const [results, setResults] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/screener/results?limit=5");
      setResults(res.data.results || []);
    } catch { /* ok */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (results.length === 0) return null;

  const latest = results[0];
  const all = [
    ...(latest.india || []).map(s => ({ ...s, market: "INDIA" })),
    ...(latest.us || []).map(s => ({ ...s, market: "US" })),
    ...(latest.crypto || []).map(s => ({ ...s, market: "CRYPTO" })),
  ];

  const ranAt = latest.ran_at
    ? new Date(latest.ran_at).toLocaleString("en-IN", { hour12: false, timeZone: "Asia/Kolkata" })
    : null;

  return (
    <Card className="bg-surface-2 border-border rounded-sm">
      <CardHeader className="pb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 font-semibold">
          / scanner results
        </div>
        <CardTitle className="text-base font-medium flex items-center gap-2">
          Latest Scan — {all.length} passed
          {ranAt && (
            <span className="text-[10px] font-mono text-muted-foreground font-normal">
              {ranAt}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {all.length === 0 ? (
          <div className="text-xs text-muted-foreground py-4 text-center">
            No stocks passed the screener criteria today. The trend gate
            (Close &gt; SMA(20) &gt; SMA(50)) and 3/4 secondary conditions
            are intentionally strict.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
                  <th className="text-left py-2 pr-4">Market</th>
                  <th className="text-left py-2 pr-4">Symbol</th>
                  <th className="text-right py-2 pr-4">Close</th>
                  <th className="text-right py-2 pr-4">RSI(14)</th>
                  <th className="text-right py-2 pr-4">SMA(20)</th>
                  <th className="text-right py-2 pr-4">SMA(50)</th>
                  <th className="text-right py-2">Score</th>
                </tr>
              </thead>
              <tbody>
                {all.map((s, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-surface-1/50">
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="text-[9px] font-mono">
                        {s.market}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 font-medium">{s.symbol}</td>
                    <td className="py-2 pr-4 text-right">{s.close?.toFixed?.(2) ?? s.close}</td>
                    <td className="py-2 pr-4 text-right">{s.rsi}</td>
                    <td className="py-2 pr-4 text-right">{s.sma_fast?.toFixed?.(2)}</td>
                    <td className="py-2 pr-4 text-right">{s.sma_slow?.toFixed?.(2)}</td>
                    <td className="py-2 text-right">{s.secondary_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
